import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { 
  hasPermission, 
  hasAnyPermission, 
  hasAnyRole, 
  isPendingUser,
  ROLES 
} from "../utils/rbac";

/**
 * Enhanced permission-based route protection component
 * 
 * This component provides fine-grained access control based on:
 * - Specific permissions
 * - Role requirements  
 * - Resource ownership
 * - Fallback routes for different scenarios
 */
export default function PermissionProtectedRoute({ 
  children, 
  requiredPermissions = [], 
  requiredRoles = [],
  requireAll = false, // If true, user must have ALL permissions/roles
  checkOwnership = false,
  fallbackRoute = "/",
  pendingRoute = "/new",
  requireAnyPermission = true, // Default behavior for backward compatibility
  ownershipContext = {},
  showUnauthorized = false // If true, show unauthorized message instead of redirecting
}) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // Determine access based on requirements
  const hasAccess = () => {
    if (!user || !user.rules) return false;

    // Handle pending users
    if (isPendingUser(user.rules)) {
      return false; // Will redirect to pending route
    }

    // Check role requirements
    if (requiredRoles.length > 0) {
      const roleAccess = requireAll 
        ? requiredRoles.every(role => hasAnyRole(user.rules, [role]))
        : hasAnyRole(user.rules, requiredRoles);
      
      if (!roleAccess) return false;
    }

    // Check permission requirements
    if (requiredPermissions.length > 0) {
      const context = {
        isOwner: checkOwnership ? checkOwnershipLogic() : false,
        ...ownershipContext
      };

      if (requireAll) {
        return requiredPermissions.every(permission => 
          hasPermission(user, permission, context)
        );
      } else {
        return requiredPermissions.some(permission => 
          hasPermission(user, permission, context)
        );
      }
    }

    // If no specific requirements, allow access for authenticated users
    return true;
  };

  const checkOwnershipLogic = () => {
    // This can be customized based on the route and resource
    // For now, basic implementation
    if (ownershipContext.userEmail && user.email) {
      return ownershipContext.userEmail === user.email;
    }
    if (ownershipContext.userId && user.userId) {
      return ownershipContext.userId === user.userId;
    }
    return false;
  };

  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate("/signin", { replace: true });
        return;
      }

      // Handle pending users specially
      if (isPendingUser(user.rules)) {
        navigate(pendingRoute, { replace: true });
        return;
      }

      // Check access permissions
      if (!hasAccess()) {
        if (!showUnauthorized) {
          navigate(fallbackRoute, { replace: true });
        }
      }
    }
  }, [
    user, 
    loading, 
    navigate, 
    requiredPermissions, 
    requiredRoles, 
    requireAll, 
    checkOwnership, 
    fallbackRoute, 
    pendingRoute,
    showUnauthorized
  ]);

  // Show loading spinner
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500 dark:border-blue-300"></div>
      </div>
    );
  }

  // Show unauthorized message if configured to do so
  if (user && !isPendingUser(user.rules) && !hasAccess() && showUnauthorized) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center p-8 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
          <h2 className="text-2xl font-bold text-red-800 dark:text-red-300 mb-4">
            Access Denied
          </h2>
          <p className="text-red-600 dark:text-red-400 mb-4">
            You don't have the required permissions to access this page.
          </p>
          {requiredPermissions.length > 0 && (
            <p className="text-sm text-red-500 dark:text-red-400 mb-2">
              Required permissions: {requiredPermissions.join(", ")}
            </p>
          )}
          {requiredRoles.length > 0 && (
            <p className="text-sm text-red-500 dark:text-red-400 mb-4">
              Required roles: {requiredRoles.join(", ")}
            </p>
          )}
          <button
            onClick={() => navigate(fallbackRoute)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // Only render children if user has access
  return user && hasAccess() ? children : null;
}

/**
 * Convenience wrapper for admin-only routes
 */
export function AdminRoute({ children, ...props }) {
  return (
    <PermissionProtectedRoute
      requiredRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
      {...props}
    >
      {children}
    </PermissionProtectedRoute>
  );
}

/**
 * Convenience wrapper for manager+ routes
 */
export function ManagerRoute({ children, ...props }) {
  return (
    <PermissionProtectedRoute
      requiredRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.MANAGER]}
      {...props}
    >
      {children}
    </PermissionProtectedRoute>
  );
}

/**
 * Convenience wrapper for vendor routes
 */
export function VendorRoute({ children, ...props }) {
  return (
    <PermissionProtectedRoute
      requiredRoles={[
        ROLES.ADMIN, 
        ROLES.SUPER_ADMIN, 
        ROLES.TOUR_PROVIDER, 
        ROLES.SERVICE_PROVIDER, 
        ROLES.GAC_VENDOR
      ]}
      {...props}
    >
      {children}
    </PermissionProtectedRoute>
  );
}
